import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, Search, UserRound } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { usePlatformDesigner } from "@/lib/usePlatformDesigner";
import { usePlatformDesignerBuyerPreview } from "@/lib/usePlatformDesignerBuyerPreview";

/**
 * Selector de cliente (titular) — solo diseñador plataforma.
 * Permite apuntar el borrado de historial al workspace correcto.
 */
export default function BntAsistenteDesignerChatTarget({
  value = null,
  onChange,
}) {
  const isPlatformDesigner = usePlatformDesigner();
  const buyerPreviewActive = usePlatformDesignerBuyerPreview();
  const [query, setQuery] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const loadTargets = useCallback(async (q) => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/asistente/clear-history/targets", {
        params: { q: q || "", limit: 40 },
        timeout: 30000,
      });
      setItems(Array.isArray(data?.items) ? data.items : []);
    } catch (err) {
      setItems([]);
      setError(formatApiError(err.response?.data?.detail) || "No se pudo cargar clientes.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isPlatformDesigner || buyerPreviewActive) return undefined;
    const t = window.setTimeout(() => {
      loadTargets(query.trim());
    }, 250);
    return () => window.clearTimeout(t);
  }, [query, isPlatformDesigner, buyerPreviewActive, loadTargets]);

  const selectedId = value?.user_id || "";

  const selectedLabel = useMemo(() => {
    if (!value) return "";
    return value.label || `${value.name || ""} · ${value.email || value.user_id || ""}`.trim();
  }, [value]);

  if (!isPlatformDesigner || buyerPreviewActive) return null;

  return (
    <div
      className="rounded-md border border-amber-200 bg-amber-50/80 p-2.5 space-y-2 shrink-0"
      data-testid="bnt-asistente-designer-chat-target"
    >
      <div className="flex items-start gap-2">
        <UserRound className="h-3.5 w-3.5 text-amber-900 mt-0.5 shrink-0" aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-semibold text-amber-950 m-0">
            Modo diseñador — cliente a auditar / purgar
          </p>
          <p className="text-[10px] text-amber-900/90 m-0 mt-0.5 leading-snug">
            El chat interno del despacho no es accesible como diseñador. Elija el titular
            (p. ej. por correo) para borrar su historial de equipo.
          </p>
        </div>
      </div>

      <div className="space-y-1">
        <Label htmlFor="bnt-designer-chat-target-q" className="text-[10px] text-amber-950">
          Buscar titular
        </Label>
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" aria-hidden />
          <Input
            id="bnt-designer-chat-target-q"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Correo o nombre (ej. despachobnt.mty@gmail.com)"
            className="rounded-sm h-8 text-xs pl-7 bg-white"
            data-testid="bnt-asistente-designer-chat-target-search"
          />
        </div>
      </div>

      {loading ? (
        <p className="text-[10px] text-slate-600 m-0 inline-flex items-center gap-1">
          <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
          Buscando…
        </p>
      ) : null}
      {error ? (
        <p className="text-[10px] text-rose-800 m-0" data-testid="bnt-asistente-designer-chat-target-error">
          {error}
        </p>
      ) : null}

      {selectedLabel ? (
        <div
          className="rounded-sm border border-emerald-200 bg-emerald-50 px-2 py-1.5 text-[10px] text-emerald-950"
          data-testid="bnt-asistente-designer-chat-target-selected"
        >
          <span className="font-semibold">Seleccionado:</span> {selectedLabel}
          {typeof value?.chat_message_count === "number" ? (
            <span className="ml-1 text-emerald-800">
              · {value.chat_message_count} mensaje(s) en chat
            </span>
          ) : null}
          <button
            type="button"
            className="ml-2 underline text-emerald-900"
            onClick={() => onChange?.(null)}
            data-testid="bnt-asistente-designer-chat-target-clear"
          >
            Quitar
          </button>
        </div>
      ) : (
        <p className="text-[10px] text-amber-900 m-0">
          Sin cliente seleccionado — el botón «Borrar historial» pedirá uno.
        </p>
      )}

      <ul
        className="max-h-36 overflow-y-auto rounded-sm border border-amber-100 bg-white divide-y divide-slate-100"
        data-testid="bnt-asistente-designer-chat-target-list"
      >
        {items.length === 0 && !loading ? (
          <li className="px-2 py-2 text-[10px] text-slate-500">Sin resultados.</li>
        ) : null}
        {items.map((item) => {
          const active = item.user_id === selectedId;
          return (
            <li key={item.user_id}>
              <button
                type="button"
                className={`w-full text-left px-2 py-1.5 text-[10px] hover:bg-slate-50 ${
                  active ? "bg-sky-50 text-sky-950" : "text-slate-800"
                }`}
                onClick={() => onChange?.(item)}
                data-testid={`bnt-asistente-designer-chat-target-item-${item.user_id}`}
              >
                <span className="font-medium block truncate">{item.name || "Sin nombre"}</span>
                <span className="text-slate-500 block truncate">
                  {item.email || item.user_id}
                  {typeof item.chat_message_count === "number"
                    ? ` · ${item.chat_message_count} msg`
                    : ""}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
